import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '@services/auth.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { I18nService } from '@core/services/i18n/i18n.service';
import { DEFAULT_LOCALE_LANG, LocaleLang } from '@core/services/i18n/locale-lang.enum';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Lang } from '@app/models/common/lang';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  modalMsgMap = new Map<string | number, Lang>([
    [
      'AuthenticationError_AccountNoEffect',
      { EN: 'Your account is not activated and has no access rights.', CN: '您的账号未激活，无访问权限。' },
    ],
    ['AuthenticationError_TokenInvalidOrExpired', { EN: 'Login expired', CN: '登录过期' }],
    [
      'AuthenticationError_SameAccountLoginExceedMaxNumber',
      { EN: 'Same account login number has exceed the max number', CN: '同一帐户的登录号已超过最大数目' },
    ],
    [
      'AuthenticationError_UserAccountHasExpired',
      { EN: 'Your account is invalid and you have no access rights.', CN: '您的账号已失效，无访问权限。' },
    ],
    ['AuthenticationError_UserAccountHasLocked', { EN: 'Account has been locked', CN: '帐户已被锁定' }],
    [
      'AuthenticationError_AccessDenied',
      { EN: "There's no valid contract for current user", CN: '当前用户没有有效的合同' },
    ],
    [
      'AuthenticationError_UserAccountHasDisabled',
      { EN: 'The account has been deactivated due to its inactivity.', CN: '该帐户由于长期未登录而被停用，请联系对接人开通权限' },
    ],
    ['AuthenticationError_BadRequest', { EN: 'Bad Request', CN: '错误的请求' }],
    [
      'AuthenticationError_Timeout',
      { EN: 'Unable to log in, some issue encountered, please try again.', CN: '无法登录，遇到一些问题，请重试。' },
    ],
    ['SSI_UPLOAD_ERROR', {EN:'', CN:''}]
  ]);

  commonMsgMap = new Map<string, Lang>([
    ['INTERNAL_SERVER_ERROR', { EN: 'No data available!', CN: '暂无数据！' }],
    ['AuthenticationError_WrongAccountOrPassword', { EN: 'Wrong account or password ', CN: '用户名或密码错误！' }],
  ]);

  currLang: LocaleLang = DEFAULT_LOCALE_LANG;
  constructor(
    private authService: AuthService,
    private notification: NzNotificationService,
    private modal: NzModalService,
    private i18nService: I18nService,
  ) {
    this.i18nService.currentLang$.subscribe(lang => {
      this.currLang = lang;
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        this.handleErrMsg(err);
        return throwError(err);
      }),
    );
  }

  async handleErrMsg(err: HttpErrorResponse) {
    let { error } = err;
    if (error instanceof Blob) {
      const text = await new Response(error).text();
      error = JSON.parse(text);
    }
    const { data, exception = {}, statusCode } = error || {};
    const { msg, msgKey } = exception || {};
    const { message } = data || {};

    if (this.modalMsgMap.has(msgKey)) {
      const modalMsg = (this.modalMsgMap.get(msgKey) || {})[this.currLang];
      if (modalMsg) {
        this.showErrorMsg(modalMsg, msgKey, true, true);
      } else {
        this.showErrorMsg(msg, msgKey, true, false);
      }
      return;
    }
    const errMsg = (this.commonMsgMap.get(msgKey) || {})[this.currLang] || message || msg;
    this.showErrorMsg(errMsg);
  }

  errModal?: NzModalRef;
  showErrorMsg(errMsg: string, msgKey?: string, isShowModal?: boolean, isSignOut?: boolean) {
    if (!errMsg) return;
    const msgArr: string[] = errMsg.split('##');
    if (isShowModal) {
      if (!this.errModal) {
        this.errModal = this.modal.error({
          nzTitle: msgArr.length === 2 ? `${msgArr[0]}` : undefined,
          nzContent: msgArr.length === 2 ? `${msgArr[1]}` : `${errMsg}`,
          nzWidth: isSignOut ? 420 : 600,
          nzClassName: msgKey ? msgKey.toLowerCase() : undefined,
          nzClosable: false,
          nzOnOk: () => {
            isSignOut && this.authService.signOut();
            this.errModal?.destroy();
            this.errModal = undefined;
          },
        });
      }
    } else {
      this.notification.error('', errMsg, { nzKey: errMsg });
    }
  }
}
